/**
 * Bootstrap hook for prefetching Inventory data after login/home mount
 * 
 * Goal: Prefetch the first page of Inventory data in the background so that
 * when the user clicks on the Inventory menu item, the data is already in cache.
 * 
 * This provides a "big CRM" experience where the Inventory page appears almost instantly.
 * 
 * Usage:
 * ```tsx
 * const { company } = useParams<{ company: string }>();
 * const { client } = useClient();
 * 
 * useBootstrapInventoryData({
 *   companyId: company,
 *   enabled: !!client && !!company,
 * });
 * ```
 */

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { inventoryKeys } from "../lib/queryKeys";
import { listItems } from "../api/rpc/inventory";
import { queryOptions } from "../lib/queryClient";

type UseBootstrapInventoryDataProps = {
  companyId: string | undefined;
  enabled?: boolean; // default true
};

// Default params for inventory prefetch (matches InventoryManagementPage initial state)
const DEFAULT_INVENTORY_QUERY_PARAMS = {
  // No search, no pagination - just get all items
  // This matches the initial state of InventoryManagementPage
};

export function useBootstrapInventoryData({ 
  companyId, 
  enabled = true 
}: UseBootstrapInventoryDataProps) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    if (!companyId) return;

    // Use the EXACT same default params as the Inventory page initial query
    // This ensures the query keys match and cached data is immediately available
    const prefetchParams = { ...DEFAULT_INVENTORY_QUERY_PARAMS };
    const queryKey = inventoryKeys.list(companyId, prefetchParams);

    // Check if data already exists in cache (before prefetching)
    const existingData = queryClient.getQueryData(queryKey);
    if (existingData) {
      return;
    }

    // Prefetch in background - don't await, let it run silently
    queryClient.prefetchQuery({
      queryKey,
      queryFn: async () => {
        const items = await listItems(companyId, {});
        
        // Return in the same format as useInventoryQuery
        return {
          items: Array.isArray(items) ? items : [],
        };
      },
      // Use same caching strategy as the actual query (must match useInventoryQuery)
      // Use queryOptions.heavyTable preset: 2min stale, 60min cache
      staleTime: 2 * 60 * 1000, // 2 minutes - matches queryOptions.heavyTable
      gcTime: 60 * 60 * 1000, // 60 minutes - matches queryOptions.heavyTable
    }).catch((error) => {
      // Silently handle errors - prefetch failures shouldn't break the app
    });
  }, [companyId, enabled, queryClient]);
}

